import { debounceTime, Subject } from 'rxjs';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { EditField } from './edit-field';
import { STG } from './stage';
import { View$, VPAR } from './view$';
// import { ViewFilter } from "./view-filter"
import { XItem } from './x-item';
import { ScriptSubset } from './script-subset';
import { Script } from './script';
import { ScriptItem } from './script-x-item';
import { ConditionItem } from './condition-item';

export type V = {
    id?: string;
    title?: string;
    xitemIdLs?: string[];
    items?: string[];
    idSet?: Set<string>;
    ownerId?: string;
    isHitset?: boolean;
    isPreview?: boolean;
    isOff?: boolean;
    script?: string;
};
export class View {
    cls = 'view';
    id: string;
    // viewFilter: ViewFilter
    ownerId: string;
    isHitset = false;
    isPreview = false;

    script?: string;
    private _scriptInstance?: Script;

    private _valueMap = new Map<string, any>();
    private _editFieldMap = new Map<string, EditField>();

    private _xitemIdSet = new Set<string>();
    v$: View$;

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    constructor(
        private coords: CoordinatorService,
        params: V,
    ) {
        this.v$ = coords.view$;
        if (params.id && params.id.startsWith('__')) {
            const prefix = params.id.split('-')[0];
            if (prefix === params.id) this.id = `${prefix}-${this.coords.project$.currentId}`;
            else this.id = params.id;
        } else {
            this.id = params.id || this.coords.uniqueId();
        }
        this.title = (params.title && params.title.trim()) || this.id;
        this.ownerId = params.ownerId || '_current';

        if (params.idSet) this._xitemIdSet = new Set(params.idSet);
        else if (params.xitemIdLs) this._xitemIdSet = new Set(params.xitemIdLs);
        else if (params.items) this._xitemIdSet = new Set(params.items);

        if (params.isHitset) {
            this.isHitset = true;
            this.isPreview = !!params.isPreview;
        }

        this.coords.view$.addToMap(this);
        this.script = params.script;
        this._compileScript();
        this._markForManifestStore$.subscribe(() => this._manifestStore());
    }

    checkCollectionItems() {
        Array.from(this._xitemIdSet).forEach((id) => {
            const colId = this.coords.dataset$.main.getMainIdfromAnyId(id);
            if (colId === id) return;
            this._xitemIdSet.delete(id);
            if (colId) this._xitemIdSet.add(colId);
        });
    }

    public get isOn() {
        return !this.coords.pin$.misc?.has(`${this.id}-off`);
    }
    public set isOn(value) {
        if (value) this.coords.pin$.misc?.off(`${this.id}-off`);
        else this.coords.pin$.misc?.on(`${this.id}-off`);

        this.coords.display$.markForCheck();
    }
    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    editField(tag: string) {
        let ef = this._editFieldMap.get(tag);
        if (!ef) {
            ef = new EditField(this.coords, tag, this);
            this._editFieldMap.set(tag, ef);
        }
        return ef;
    }

    private _manifestStore() {
        const valueMap = new Map<string, any>([
            ['title', this.title],
            ['xitemIdLs', Array.from(this._xitemIdSet)],
        ]);

        this.script && valueMap.set('script', this.script);

        if (!this.isOn) valueMap.set('isOff', true);

        this.coords.manifest$.store({
            type: 'view',
            id: this.id,
            valueMap: valueMap,
        });
    }

    get(tag: string) {
        return this._valueMap.get(tag);
    }

    set(tag: string, value: any) {
        if (this.isOwned) {
            this._valueMap.set(tag, value);
            this.markForManifestStore();
        }
    }

    get isOwned() {
        return this.ownerId === this.coords.user$.currentId || this.ownerId === '_current';
    }

    get idSet() {
        return new Set(this._xitemIdSet);
    }

    get activeIdLs() {
        const s = this.coords.dataset$.current.mainIdSet;
        return Array.from(this._xitemIdSet).filter((id) => s.has(id));
    }

    public get title(): string {
        if (this.id.startsWith('__'))
            return this.coords.text(`subset-${this.id.split('-')[0].slice(2)}`);
        return this._valueMap.get('title');
    }
    public set title(value: string) {
        this._valueMap.set('title', value);
    }

    has(xitmId: string) {
        return this._xitemIdSet.has(xitmId);
    }

    addXItemLs(ls: XItem[]) {
        this.addIds(ls.map((x) => x.id));
    }

    deleteXItemLs(ls: XItem[]) {
        this.deleteIds(ls.map((x) => x.id));
    }

    switchXItemLs(ls: XItem[]) {
        const addIdLs: string[] = [];
        const deleteIdLs: string[] = [];

        ls.forEach((xitm) => {
            const ls = this.has(xitm.id) ? deleteIdLs : addIdLs;
            ls.push(xitm.id);
        });
        this.addIds(addIdLs);
        this.deleteIds(deleteIdLs);
    }

    addIds(s: string | string[] | Set<string>, withRefresh = true) {
        if (typeof s == 'string') s = [s];

        if (this.id.startsWith('__toMain')) {
            this.coords.dataset$.addXitemLsToMain(
                this.coords.dataset$.preview.xitemLsFromIdLs(s as string[]),
                this.coords.user$.currentId,
            );
        } else {
            s.forEach((x) => this._xitemIdSet.add(x));
            if (this.isRejected) this.coords.view$.get('__seen')?.addIds(s);
        }
        withRefresh && this._refresh();
    }

    deleteIds(s: string | string[] | Set<string>, withRefresh = true) {
        if (typeof s == 'string') s = [s];

        if (this.id.startsWith('__fromMain')) {
            this.coords.dataset$.main.deleteXitemIdLs(s as string[]);
        } else if (this.id.startsWith('__toMain')) {
            this.coords.dataset$.main.deleteXitemIdLs(
                (s as string[])
                    .map((x) => this.coords.dataset$.fromPreview2MainId(x)!)
                    .filter((x) => x),
            );
        } else {
            s.forEach((x) => this._xitemIdSet.delete(x));
        }
        withRefresh && this._refresh();
    }

    get isRejected() {
        return this.id.startsWith('__rejected');
    }

    // get isSeen() {
    //     return this.id == "__seen"
    // }

    clear() {
        this._xitemIdSet.clear();
    }

    switchIds(s: string | string[] | Set<string>, withRefresh = true) {
        let seenView: View | undefined;

        if (!this.isOwned) return;

        if (this.isRejected) seenView = this.coords.view$.get('__seen');
        if (typeof s == 'string') s = [s];
        s.forEach((x) => {
            if (this._xitemIdSet.delete(x)) return;
            this._xitemIdSet.add(x);
            seenView && seenView.addIds(x);
        });
        withRefresh && this._refresh();
    }

    countInSet(s: Set<string>) {
        let c = 0;
        this._xitemIdSet.forEach((x) => s.has(x) && c++);
        return c;
    }

    get mainXitemLs() {
        return this.coords.dataset$.main.mainXitemLs.filter((x) => this.has(x.id));
    }

    testLs(s: string[] | Set<string>) {
        if (Array.isArray(s)) s = new Set(s);
        const ls: string[] = [];
        this._xitemIdSet.forEach((x) => (<Set<string>>s).has(x) && ls.push(x));
        return ls;
    }

    save(): VPAR {
        const dct: any = {
            id: this.id,
            title: this.title,
            xitmIdLs: Array.from(this._xitemIdSet),
        };
        this.script && (dct.script = this.script);
        return dct;
    }

    overlapData() {
        const dt = this.isPreview ? this.coords.dataset$.preview : this.coords.dataset$.main;
        const s = new Set<string>();
        this._xitemIdSet.forEach((xitmId) => {
            const finalId = dt.getMainIdfromAnyId(xitmId);
            finalId && s.add(finalId);
        });

        return {
            id: this.id,
            type: this.isHitset ? 'hitset' : 'view',
            dataset: this.isPreview ? 'preview' : 'main',
            title: this.title,
            idLs: Array.from(s),
        };
    }

    private _refresh() {
        Array.from(this._xitemIdSet).forEach((xitmId) => {
            if (this.id.startsWith('_')) return;
            if (this.coords.dataset$.main.has(xitmId, true)) return;
            this._xitemIdSet.delete(xitmId);
        });
        this.markForManifestStore();
        this.coords.display$.markForCheck();
        !this.id.startsWith('_') && this.coords.searchArchive$.clearCacheHitset();
    }

    manifestDelete() {
        const ls = [
            {
                id: this.id,
                type: 'view',
            },
        ];
        this.coords.manifest$.delete(ls);
    }

    delete() {
        !this.id.startsWith('_') && this.coords.searchArchive$.clearCacheHitset();
        this.coords.view$.delete(this);
        this.manifestDelete();
        this.coords.view$.refresh();
        this.coords.display$.restartDialog();
        this.coords.display$.markForCheck();
    }

    get deleteParams() {
        return {
            icon: 'delete',
            fun: () => this.delete(),
        };
    }

    get size() {
        return this._xitemIdSet.size;
    }

    preview() {
        let pTag = 'main' as 'main' | 'preview';
        let icon = 'subset';
        let cls = 'selection-in-general';
        let prefix = this.coords.text('subset');

        let title = this.title;

        switch (this.id.substring(0, this.id.indexOf('-'))) {
            case '__seen':
                pTag = 'preview';
                prefix = '';
                icon = 'counter_seen';
                cls = 'selection-in-seen';
                title = this.coords.text('Seen');
                break;
            case '__rejected':
                pTag = 'preview';
                prefix = '';
                icon = 'counter_rejected';
                cls = 'selection-in-rejected';
                title = this.coords.text('Rejected');
                break;
            case '__bookmarked':
                prefix = '';
                icon = 'addToBookmarkedIn';
                cls = 'selection-in-bookmarked';
                title = this.coords.text('Bookmarked');
                break;
        }

        const stg = this.coords.stage$.current!.tag;

        this.coords.resultList$.activateSubset({
            idSet: this._xitemIdSet,
            title: title,
            prefix: prefix,
            icon: icon,
            class: cls,
            back: () => this.coords.stage$.set(stg),
            stage: pTag === 'main' ? 'collection' : 'preview',
        });
    }

    conditionEditor() {
        this.coords.condition$.formulaEditor(this);
    }

    testItem(xitm: XItem) {
        if (!this._scriptInstance) return true;
        return this._scriptInstance.run({ item: xitm.scriptClass });
    }

    getScript() {
        return this.script || '';
    }

    private _compileScript() {
        if (!this.script?.trim()) return;
        this._scriptInstance = new Script(this.coords, {
            source: this.script,
        });
    }

    submitScript(data: string) {
        this.script = data;
        this._compileScript();
        this.coords.display$.markForCheck();
        this.markForManifestStore();
    }

    scriptValue({ item, conditionItem }: { item: ScriptItem; conditionItem?: ConditionItem }) {
        if (!this.script) return true;
        try {
            return this._scriptInstance?.run({ item });
        } catch (error) {
            console.error('FORMULA ERROR', this.script, error);
        }
    }

    get helpHtml() {
        const ls: string[] = [];
        ls.push(`<b>${this.coords.text('Size')}: </b>${this.size}`);
        for (const x of this._xitemIdSet) {
            const dt = this.coords.dataset$.main;
            const xitm = x && dt.get(x);
            xitm && ls.push(xitm.title);
            if (ls.length >= 7) {
                ls.push('...');
                break;
            }
        }
        return ls.map((x) => `<div class='one-line'>${x}</div>`).join('\n');
    }

    get scriptClass() {
        return new ScriptSubset(this.coords, this);
    }
}
